import type { AuthState } from '../auth/auth-store';

/** Where the app goes after a 401: sign-in with the way back, or home. */
export type AfterRefusal = { to: '/sign-in'; search: { next: string } } | { to: '/' };

export interface SessionRefusedDeps {
  auth: { getState: () => AuthState; recheck: () => Promise<void> };
  /** The address the person is on, path and query. */
  here: () => string;
  go: (where: AfterRefusal) => void;
}

/** The company a tenant-scoped address names (`/t/{id}/…`), or null. */
export function companyIn(href: string): string | null {
  return /^\/t\/([^/?#]+)/.exec(href)?.[1] ?? null;
}

/**
 * What a 401 means is for `GET /me` to say, never the refused request. The API also answers 401
 * when the session is fine but is no longer in the company the request named (the person left it,
 * was removed, or it was closed): ending the session on that word left a signed-in person on the
 * failure screen. Signed out for real goes to sign-in with the way back; out of that company goes
 * home, which picks one they still have; anything else stays where it is. A burst of 401s asks once.
 */
export function sessionRefused(deps: SessionRefusedDeps): () => void {
  let asking: Promise<void> | null = null;
  const settle = async (): Promise<void> => {
    const here = deps.here();
    await deps.auth.recheck();
    const { status, principal } = deps.auth.getState();
    if (status === 'signed-out') return deps.go({ to: '/sign-in', search: { next: here } });
    const company = companyIn(here);
    if (!principal || !company || principal.staff) return;
    if (!principal.tenants.some((t) => t.id === company)) deps.go({ to: '/' });
  };
  return () => {
    if (deps.auth.getState().status !== 'signed-in') return;
    asking ??= settle().finally(() => (asking = null));
  };
}
